import React, { useMemo, useRef } from 'react';
import { useMapVisibilityStore } from '@pkgs/components/providers/MapVisibilityStoreProvider';
import { useIsLipaPage } from '@pkgs/components/hooks/useIsLipaPage';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { SearchMode } from '@pkgs/components/hooks/stores/search/slices/searchModeSlice';
import { usePlaceDetails } from '@pkgs/components/hooks/queries/usePlaceDetails';
import { useListings } from '@pkgs/components/hooks/queries/useListings';
import { useAiSearch } from '@pkgs/components/hooks/queries/useAiSearch';
import { SortingOptionId } from '@pkgs/components/enums/enums';
import { Pagination } from '@ev-tech/ui-core-components/Pagination';
import { BusinessArea, MarketCountryCode } from '@ev-tech/search-modules-api';

import { SearchResultsListData } from './SearchResultsListData';
import { WorkEditionCtaCard } from '../WorkEditionCtaCard/WorkEditionCtaCard';
import { LoadingResultCard } from '../Skeleton/LoadingResultCard';
import { SearchAlertCtaCard } from '../SearchAlertCtaCard/SearchAlertCtaCard';
import { PrivateOfficeCtaCard } from '../PrivateOfficeCtaCard/PrivateOfficeCtaCard';
import { NoResultsBanner } from '../NoResultsBanner/NoResultsBanner';
import { LipaCtaCard } from '../LipaCtaCard/LipaCtaCard';

import styles from './SearchResults.module.css';

const SKELETON_CARDS = Array.from({ length: 18 }, (_, index) => <LoadingResultCard key={index} />);

type Props = {
  onClick?: () => void;
  isMapView: boolean;
};

export const shouldShowWorkEditionCard = ({
  countryCode,
  businessArea,
  placeDetailsList,
}: {
  countryCode: MarketCountryCode;
  businessArea: BusinessArea | 'development';
  placeDetailsList: Array<{ showWorkEdition?: boolean }>;
}) =>
  Boolean(
    countryCode === MarketCountryCode.DE &&
    businessArea === BusinessArea.commercial &&
    placeDetailsList.some((placeDetails) => placeDetails.showWorkEdition),
  );

export const SearchResultList = React.memo(({ onClick, isMapView }: Props) => {
  const { isFetching: isListingsFetching, data } = useListings();
  const { isPending: isAiSearchFetching, isError } = useAiSearch();
  const gridContainerRef = useRef<HTMLDivElement | null>(null);
  const isMapActive = useMapVisibilityStore((state) => state.isMapActive);
  const isLipaPage = useIsLipaPage();
  const { countryCode } = useAppContext();
  const businessArea = useSearchStore((state) => state.objectTypeCategory);
  const page = useSearchStore((state) => state.page);
  const setPage = useSearchStore((state) => state.setPage);
  const pageSize = useSearchStore((state) => state.pageSize);
  const sortingOptionId = useSearchStore((state) => state.sortingOptionId);

  const { placeDetailsList } = usePlaceDetails();

  const isFetching = useMemo(() => isListingsFetching || isAiSearchFetching, [isListingsFetching, isAiSearchFetching]);

  const searchMode = useSearchStore((state) => state.searchMode);
  const isAiSearchError = isError && searchMode === SearchMode.AI;

  const showWorkEditionCard = useMemo(
    () =>
      shouldShowWorkEditionCard({
        countryCode,
        businessArea,
        placeDetailsList,
      }),
    [countryCode, businessArea, placeDetailsList],
  );
  const showPrivateOfficeCard = countryCode === MarketCountryCode['private-office'];

  const numOfResults = data.listings.length;

  const calculatedPages = pageSize === 0 ? 0 : Math.ceil(data.listingsTotal / pageSize);
  const totalPages =
    sortingOptionId === SortingOptionId.SMART_SORT || sortingOptionId === SortingOptionId.PREFERENCES
      ? Math.min(calculatedPages, 20)
      : calculatedPages;

  if ((data.listings.length === 0 || isAiSearchError) && !isFetching) return <NoResultsBanner isAISearchError={isAiSearchError} />;

  return (
    <div className={styles.resultsListContainerWrapper}>
      <div ref={gridContainerRef} className={styles.resultsListContainer} data-map-view={isMapView}>
        {isFetching ? (
          SKELETON_CARDS
        ) : (
          <>
            <SearchResultsListData gridContainerRef={gridContainerRef} />

            {isLipaPage ? (
              <div
                className={styles.variableCtaContainer}
                data-hide-to-tablet-portrait={numOfResults <= 3 ? true : undefined}
                data-hide-from-tablet-portrait={numOfResults <= 4 ? true : undefined}
                data-hide-from-laptop={numOfResults <= 6 ? true : undefined}
                data-map-active={isMapActive ? true : undefined}
                data-hide-from-laptop-map={numOfResults <= 4 ? true : undefined}
              >
                <LipaCtaCard key="lipa-cta-card" className={styles.ctaCard} />
              </div>
            ) : (
              <div className={styles.variableCtaContainer} data-map-active={isMapActive ? true : undefined}>
                <SearchAlertCtaCard key="search-alert-cta-card" className={styles.ctaCard} onClick={onClick} />
              </div>
            )}

            {showWorkEditionCard && (
              <div
                className={styles.workEditionCtaContainer}
                data-hide-to-tablet-portrait={numOfResults <= 5 ? true : undefined}
                data-hide-from-tablet-portrait={numOfResults <= 6 ? true : undefined}
                data-hide-from-laptop={numOfResults <= 9 ? true : undefined}
                data-map-active={isMapActive ? true : undefined}
                data-hide-from-laptop-map={numOfResults <= 6 ? true : undefined}
              >
                <WorkEditionCtaCard key="work-edition-cta-card" className={styles.ctaCard} />
              </div>
            )}

            {showPrivateOfficeCard && (
              <div className={styles.privateOfficeCtaContainer} data-map-active={isMapActive ? true : undefined}>
                <PrivateOfficeCtaCard key="private-office-cta-card" className={styles.ctaCard} />
              </div>
            )}
          </>
        )}
      </div>
      <Pagination
        totalPages={totalPages}
        currentPage={page}
        onPageChange={setPage}
        renderAsButtons
        disabledPages={page < totalPages - 2 ? [totalPages] : undefined}
      />
    </div>
  );
});

SearchResultList.displayName = 'SearchResultList';
